import { ComponentProps, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { collectFormErrors } from '../../../../utils/collect-form-errors';
import { Button } from '../../../atoms/button/button';
import { FormError } from '../../../atoms/form-error/form-error';
import { Header } from '../../../atoms/header/header';
import { Input } from '../../../atoms/input/input';
import { Label } from '../../../atoms/label/label';
import { Text } from '../../../atoms/text/text';
import { PasswordRequirements } from '../../../molecules/password-requirements/password-requirements';
import { createSubmitHandler } from '../handle-submit';
import { PASSWORD_MIN_LENGTH, PASSWORD_PATTERN } from '../password-validation';

type Inputs = {
  currentPassword: string;
  newPassword: string;
  repeatPassword: string;
};

const CurrentPasswordField = ({ variant, ...props }: ComponentProps<typeof Input>) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor="currentPassword">{t('page.account.changePasswordForm.currentPassword')}</Label>
      <Input
        placeholder={t('page.account.changePasswordForm.currentPassword')}
        type="password"
        variant={variant}
        {...props}
      />
    </div>
  );
};

const NewPasswordField = ({
  setShowPassword,
  variant,
  ...props
}: ComponentProps<typeof Input> & {
  readonly setShowPassword: (cb: (s?: boolean) => boolean) => void;
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor="newPassword">{t('page.account.changePasswordForm.newPassword')}</Label>
      <Input
        placeholder={t('auth.common.passwordPlaceholder')}
        suffix={
          <Text className="text-neutral-700 cursor-pointer" onClick={() => setShowPassword((s) => !s)} tag="p">
            {t('auth.common.showPassword')}
          </Text>
        }
        variant={variant}
        {...props}
      />
      <PasswordRequirements />
    </div>
  );
};

const RepeatPasswordField = ({
  setShowPassword,
  variant,
  ...props
}: ComponentProps<typeof Input> & {
  readonly setShowPassword: (cb: (s?: boolean) => boolean) => void;
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor="repeatPassword">{t('page.account.changePasswordForm.repeatPassword')}</Label>
      <Input
        placeholder={t('auth.common.passwordRepeatPlaceholder')}
        suffix={
          <Text className="text-neutral-700 cursor-pointer" onClick={() => setShowPassword((s) => !s)} tag="p">
            {t('auth.common.showPassword')}
          </Text>
        }
        variant={variant}
        {...props}
      />
    </div>
  );
};

const FormContent = ({
  errors,
  newPasswordInputType,
  register,
  repeatPasswordInputType,
  setShowNewPassword,
  setShowRepeatPassword,
  t,
  watchNewPassword,
}: {
  readonly errors: Partial<Record<keyof Inputs, { message?: string }>>;
  readonly newPasswordInputType: string;
  readonly register: ReturnType<typeof useForm<Inputs>>['register'];
  readonly repeatPasswordInputType: string;
  readonly setShowNewPassword: (cb: (s?: boolean) => boolean) => void;
  readonly setShowRepeatPassword: (cb: (s?: boolean) => boolean) => void;
  readonly t: (key: string) => string;
  readonly watchNewPassword: string;
}) => (
  <div className="flex flex-col gap-[30px]">
    <Header level="h2">{t('page.account.changePassword')}</Header>
    <CurrentPasswordField
      variant={errors.currentPassword ? 'error' : 'default'}
      {...register('currentPassword', {
        required: t('auth.common.errors.passwordRequired'),
      })}
    />
    <NewPasswordField
      setShowPassword={setShowNewPassword}
      type={newPasswordInputType}
      variant={errors.newPassword ? 'error' : 'default'}
      {...register('newPassword', {
        required: t('auth.common.errors.missingPasswordFields'),
        pattern: {
          value: PASSWORD_PATTERN,
          message: t('auth.common.errors.passwordFormatDescription'),
        },
        minLength: {
          value: PASSWORD_MIN_LENGTH,
          message: t('auth.common.errors.passwordTooShort'),
        },
      })}
    />
    <RepeatPasswordField
      setShowPassword={setShowRepeatPassword}
      type={repeatPasswordInputType}
      variant={errors.repeatPassword ? 'error' : 'default'}
      {...register('repeatPassword', {
        required: t('auth.common.errors.passwordTooShort'),
        validate: (value) => value === watchNewPassword || t('auth.common.errors.passwordsDoNotMatch'),
      })}
    />
  </div>
);

const FormActions = ({
  formErrors,
  isSubmitting,
  t,
}: {
  readonly formErrors: string[];
  readonly isSubmitting: boolean;
  readonly t: (key: string) => string;
}) => (
  <div className="flex flex-col gap-4 mt-7.5">
    <FormError error={formErrors.length > 0 ? formErrors : null} icon="alert-circle" />
    <Button className="w-full" loading={isSubmitting} type="submit">
      {t('common.buttons.continue')}
    </Button>
  </div>
);

const ChangePasswordForm = ({
  onSubmit,
  onSuccess,
}: {
  readonly onSubmit: SubmitHandler<Inputs>;
  readonly onSuccess?: () => void;
}) => {
  const { t } = useTranslation();
  const [showNewPassword, setShowNewPassword] = useState<boolean>();
  const [showRepeatPassword, setShowRepeatPassword] = useState<boolean>();
  const {
    handleSubmit,
    register,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<Inputs>();
  const watchNewPassword = watch('newPassword');
  const newPasswordInputType = showNewPassword ? 'text' : 'password';
  const repeatPasswordInputType = showRepeatPassword ? 'text' : 'password';
  const formErrors = collectFormErrors(errors, t);

  const submitHandler = handleSubmit(createSubmitHandler({ onSubmit, onSuccess, setError }));

  return (
    <form onSubmit={submitHandler}>
      <FormContent
        errors={errors}
        newPasswordInputType={newPasswordInputType}
        register={register}
        repeatPasswordInputType={repeatPasswordInputType}
        setShowNewPassword={setShowNewPassword}
        setShowRepeatPassword={setShowRepeatPassword}
        t={t}
        watchNewPassword={watchNewPassword}
      />
      <FormActions formErrors={formErrors} isSubmitting={isSubmitting} t={t} />
    </form>
  );
};

export { ChangePasswordForm };
